import { PlayArrow } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { Button, Link as MuiLink, Stack, Typography } from "@mui/material";
import { makeStyles } from "components/providers/makeStyles";
import { T } from "components/util/t";
import LogoPattern from "./watch-live-logo-pattern.svg?react";
import { OptimizedImage } from "components/elements/OptimizedImage";
import { useQuery } from "@apollo/client";
import { GET_STREAMS_BY_EVENT_ID } from "./Streams.gql";
import { GetStreamsByEventIdQuery } from "components/routes/events/id/watch/Streams.gql.types";

const useStyles = makeStyles((theme) => ({
    root: {
        "--color": theme.palette.primary.light,
        "--offset": 0,
        "--transform": "translateY(0)",
        "&:hover": {
            "--color": theme.palette.secondary.main,
            "--offset": theme.spacing(1),
            "--transform": `translateY(-${theme.spacing(1)})`,
        }
    },
    button: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1.5),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(0.5),
        color: "var(--color)",

        "&:hover": {
            opacity: 1,
        }
    },
    thumbnailContainer: {
        position: "relative",
        transform: "var(--transform)",
        transition: theme.transitions.create(["transform"], {
            duration: theme.transitions.duration.standard,
        }),
        overflow: "hidden",
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(-2.5),
    },
    thumbnail: {
        display: "block",
        height: theme.spacing(5.5),
        width: "100%",
        objectFit: "cover",
        objectPosition: "center",
    },
    logoPattern: {
        position: "absolute",
        top: 0,
        right: theme.spacing(-2.25),
        bottom: 0,
        width: "auto",
        height: "20vw",
        minHeight: "100%",
        maxHeight: "200%",
        transform: "translateX(calc(var(--offset) * 4))",
        transition: theme.transitions.create(["transform"], {
            duration: theme.transitions.duration.standard,
        }),
    },
    serviceContainer: {
        display: "flex",
        width: "fit-content",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(0.5),
        wordBreak: "break-word",
    },
    serviceImage: {
        maxWidth: "110px",
        maxHeight: "12px",
        objectFit: "contain",
        objectPosition: "left",
    }
}));

export const WatchLiveButton: React.FC<{ eventId: string }> = ({ eventId }) => {
    const { data } = useQuery<GetStreamsByEventIdQuery>(GET_STREAMS_BY_EVENT_ID, {
        variables: { id: eventId }
    });
    const classes = useStyles();

    const stream = data?.event?.streams?.[0];

    if (!stream) return null;
    
    return <MuiLink component={Link} to={`/events/${eventId}/watch`} className={classes.root}>
        {stream.metadata?.thumbnailUrl && <div className={classes.thumbnailContainer}>
            <img className={classes.thumbnail} src={stream.metadata?.thumbnailUrl} alt="Live stream preview" />
            <LogoPattern className={classes.logoPattern} data-testid="logo-pattern" />
        </div>}
        <Button
            variant="contained"
            color="primary"
            fullWidth
            component="div"
            className={classes.button}
        >
            <Typography variant="button">
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <PlayArrow fontSize='small' /> <T>Watch live</T>
                </Stack>
            </Typography>
            {(stream.metadata?.serviceText || stream.metadata?.serviceImageUrl) && <Typography variant="caption" color="text.contrastSecondary" className={classes.serviceContainer}>
                {stream.metadata?.serviceText && <span>{stream.metadata.serviceText}</span>}
                {stream.metadata?.serviceImageUrl && <OptimizedImage className={classes.serviceImage} height={12} src={stream.metadata.serviceImageUrl} alt={stream.metadata.serviceText || "Streaming service logo"} />}
            </Typography>}
        </Button>
    </MuiLink>;
};
